import React, {ReactElement, useEffect, useContext} from 'react';
import './index.scss';
import {Snackbar} from '@mui/material';
import {Button, Alert, AlertTitle} from '@customink/pigment-react';
import {Link} from 'react-router-dom';
import InfoIcon from '../../assets/icons/info.svg';
import {
  useGetStoreDetailsQuery,
  useUpdateStoreMutation
} from '../../api/storesApi';
import {useAppSelector} from '../../hooks';
import {StoreUpdateParamsType} from '../../models/storeDetails';
import FirstOpenDialog from '../FirstOpenDialog';
import CopyLinkTextButton from '../CopyLinkTextButton';
import {MobileContext} from '../../containers/MobileProvider';
import services from '../../api/remoteServices';

type StoreStatusBarProps = {
  storeSlug: string;
};

const StoreStatusBar: React.FC<StoreStatusBarProps> = ({
  storeSlug
}: StoreStatusBarProps): ReactElement => {
  useGetStoreDetailsQuery(storeSlug);
  const storeDetails = useAppSelector(state => state.stores.storeDetails);
  const [firstOpen, setFirstOpen] = React.useState(false);
  const [storeStatusAlert, setStoreStatusAlert] = React.useState(false);
  const [statusAlertMessage, setStatusAlertMessage] = React.useState('');
  const [triggerUseUpdateStoreMutation, {isSuccess: isUpdateStoreSuccess}] =
    useUpdateStoreMutation();
  const mobile = useContext(MobileContext);
  const storeStatusBarClasses = `storeStatusBar${mobile ? ' mobile' : ''}`;
  const storeStatusBarActionsClasses = `storeStatusBar-actions${
    mobile ? ' mobile' : ''
  }`;

  const handleOpenStoreClick = () => {
    if (storeDetails.status === 'draft') {
      setFirstOpen(true);
    }

    const storeParams: StoreUpdateParamsType = {
      status: storeDetails.status === 'open' ? 'closed' : 'open'
    };

    triggerUseUpdateStoreMutation({details: storeParams, slug: storeSlug});
  };

  useEffect(() => {
    if (isUpdateStoreSuccess && !firstOpen) {
      setStatusAlertMessage(() =>
        storeDetails.status === 'open'
          ? 'Your store is now open!'
          : 'Your store is now closed.'
      );
      setStoreStatusAlert(true);
    }
  }, [storeDetails?.status]);

  const handleDialogClose = () => {
    setFirstOpen(false);
  };

  const handleStatusAlertClose = () => {
    setStoreStatusAlert(false);
  };

  const getContentForStatus = (storeStatus: string) => {
    if (storeStatus === 'draft')
      return (
        <span>
          Your store is in <strong>draft</strong> mode and only visible to you.
          <Link
            to={`/${storeSlug}/manage/products`}
            className="statusDraftProductLink">
            &nbsp;Add at least one product
          </Link>
          , then open your store and start selling!
        </span>
      );
    if (storeStatus === 'open')
      return (
        <span>
          Your store is <strong>open</strong>! Watch your sales pour in, adjust
          your products, or change the look and feel of your store at any time.
        </span>
      );
    return (
      <span>
        Your store is currently <strong>closed</strong>. You can open your store
        at any time to make it public.
      </span>
    );
  };

  const getButtonText = (storeStatus: string) => {
    if (storeStatus === 'draft') return 'Open Store';
    if (storeStatus === 'open') return 'Close Store';
    return 'Reopen Store';
  };

  const storeUrl = `${services.stores}/${storeSlug}`;

  return (
    storeDetails && (
      <div className={storeStatusBarClasses}>
        {!mobile ? (
          <span className="storeStatusBar-info">
            <img src={InfoIcon} alt="Info" />
            <span className="storeStatusBar-message">
              {getContentForStatus(storeDetails.status)}
            </span>
          </span>
        ) : null}

        <div className={storeStatusBarActionsClasses}>
          <CopyLinkTextButton copyLink={storeUrl} variant="link" />
          <Button
            className="previewLink"
            variant="secondary"
            size="small"
            href={storeUrl}
            target="_blank"
            rel="noreferrer">
            View Store
          </Button>
          <span className="storeStatusBar-button">
            <Button size="small" onClick={handleOpenStoreClick}>
              {getButtonText(storeDetails.status)}
            </Button>
            <FirstOpenDialog
              storeSlug={storeSlug}
              open={firstOpen}
              onClose={handleDialogClose}
            />
          </span>
        </div>
        <Snackbar
          anchorOrigin={{
            vertical: 'top',
            horizontal: 'center'
          }}
          open={storeStatusAlert}
          autoHideDuration={4000}
          onClose={handleStatusAlertClose}
          key={statusAlertMessage}>
          <div className="statusAlert-container">
            <Alert severity="success" onClose={handleStatusAlertClose}>
              <AlertTitle>{statusAlertMessage}</AlertTitle>
            </Alert>
          </div>
        </Snackbar>
      </div>
    )
  );
};

export default StoreStatusBar;
